import React, { useCallback, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { PlusOutlined } from '@ant-design/icons';

import { useExistingValues } from '@/shared/hooks/useExistingValues';
import { VALIDATION_MESSAGES } from '@/shared/lib/constants';
import { mapTagsToBasicInfo } from '@/shared/lib/tag';
import { buildAssetDetailUrl } from '@/shared/lib/url';
import type { TagSource } from '@/shared/types/openmetadata';
import { BadgeList } from '@/shared/ui/BadgeList';
import type { TreeSelectNode } from '@/shared/ui/EditableTreeSelect';
import { Icon } from '@/shared/ui/Icon';
import { IconButton } from '@/shared/ui/IconButton';
import { MetadataEditModal } from '@/shared/ui/MetadataEditModal';
import { Tooltip } from '@/shared/ui/Tooltip';

import { filterEntityLabels } from '@/entities/search';

import type { ColumnSchema } from '@/features/asset-detail/api/schemaApi';

import styles from './GlossaryTermsCell.module.css';

export type GlossaryTermsCellProps = {
  tags: ColumnSchema['tags'];
  columnName: string;
  tableFqn: string;
  glossaryTree: TreeSelectNode[];
  updateColumnTags: (params: {
    tableFqn: string;
    columnName: string;
    tagFQNs: string[];
    source: TagSource;
  }) => Promise<void>;
  isUpdating?: boolean;
};

export const GlossaryTermsCell: React.FC<GlossaryTermsCellProps> = React.memo(
  ({ tags, columnName, tableFqn, glossaryTree, updateColumnTags, isUpdating = false }) => {
    const navigate = useNavigate();
    const glossaryTerms = useMemo(
      () =>
        filterEntityLabels(tags, {
          includeOnlySource: 'Glossary',
          requireConfirmed: true,
        }),
      [tags]
    );

    const termFQNs = useMemo(() => glossaryTerms.map((term) => term.tagFQN), [glossaryTerms]);

    const existingTermFQNs = useExistingValues(termFQNs);

    const handleSaveTerms = useCallback(
      async (newTermFQNs: string[]) => {
        await updateColumnTags({
          tableFqn,
          columnName,
          tagFQNs: newTermFQNs,
          source: 'Glossary',
        });
      },
      [updateColumnTags, tableFqn, columnName]
    );

    const badgeItems = useMemo(() => mapTagsToBasicInfo(glossaryTerms), [glossaryTerms]);

    const renderTermDisplay = useCallback(() => {
      if (badgeItems.length === 0)
        return (
          <Tooltip title="용어 추가" placement="bottom">
            <div className={styles.addButtonContainer}>
              <PlusOutlined className={styles.addButtonIcon} />
              <span
                role="button"
                tabIndex={0}
                aria-label="태그 추가"
                className={styles.addButton}
                onClick={() => setIsEditing(true)}>
                추가
              </span>
            </div>
          </Tooltip>
        );
      return (
        <BadgeList
          isEllipsis={false}
          items={badgeItems}
          onItemClick={(fqn) => {
            const url = buildAssetDetailUrl('glossaryTerm', fqn);
            navigate(url);
          }}
          iconName="book"
          existingValues={existingTermFQNs}
          maxVisibleCount={5}
        />
      );
    }, [badgeItems, navigate, existingTermFQNs]);

    const [isEditing, setIsEditing] = useState(false);

    const treeSelectValue = useMemo(() => {
      return glossaryTerms.map((term) => ({
        value: term.tagFQN,
        label: term.displayName ?? term.name,
      }));
    }, [glossaryTerms]);

    return (
      <div className={styles.glossaryTermsCell}>
        {renderTermDisplay()}
        <IconButton size="small" onClick={() => setIsEditing(true)} tooltip="용어 편집">
          <Icon name="edit" size={16} />
        </IconButton>
        <MetadataEditModal
          checkStrictly={true}
          open={isEditing}
          icon={<Icon name="book" size={12} />}
          title="용어 편집"
          firstLabel="선택된 용어"
          secondLabel="용어"
          searchValidation={{
            maxLength: 256,
            warningMessage: VALIDATION_MESSAGES.GLOSSARY_SEARCH_MAX_LENGTH,
          }}
          loading={isUpdating}
          treeSelectValue={treeSelectValue}
          treeData={glossaryTree}
          onClose={() => setIsEditing(false)}
          onSubmit={handleSaveTerms}
        />
      </div>
    );
  }
);

GlossaryTermsCell.displayName = 'GlossaryTermsCell';
